import { useState } from "react";
import { Star, BadgeCheck, MapPin, ChevronDown, Camera, MessageSquareQuote } from "lucide-react";
import { REVIEWS, REVIEW_SUMMARY } from "@/data/reviews";
import { useT } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import useGoogleReviews from "@/hooks/useGoogleReviews";

/** Multicoloured Google "G" logo. */
function GoogleG({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>
  );
}

function Stars({ n = 5, size = "sm" }) {
  const cls = size === "lg"
    ? "w-5 h-5 fill-[#FBBC04] stroke-[#FBBC04]"
    : "w-4 h-4 fill-[#FBBC04] stroke-[#FBBC04]";
  return (
    <div className="inline-flex items-center gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} className={cls} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function ReviewCard({ r, t }) {
  const [showReply, setShowReply] = useState(false);
  return (
    <article
      data-testid={`review-card-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="review-card w-[300px] sm:w-[360px] shrink-0 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl p-5 sm:p-6 flex flex-col shadow-[0_10px_30px_-18px_rgba(0,0,0,0.25)]"
    >
      {/* Header — Google-style */}
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-lg shrink-0"
          style={{ backgroundColor: r.color }}
          aria-hidden="true"
        >
          {r.initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-[15px] text-[#0A0A0A] dark:text-white truncate">
              {r.name}
            </span>
            <BadgeCheck className="w-4 h-4 text-[#4285F4] shrink-0" strokeWidth={2.4} />
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-neutral-500 mt-0.5">
            {r.localGuide && (
              <span className="inline-flex items-center gap-1 text-[#0F9D58] font-semibold">
                <MapPin className="w-3 h-3" strokeWidth={2.4} />
                {t.reviews.localGuide}
              </span>
            )}
            {r.localGuide && <span className="opacity-60">·</span>}
            <span>{r.reviewCount} {r.reviewCount === 1 ? t.reviews.review : t.reviews.reviews}</span>
            {r.photos > 0 && (
              <>
                <span className="opacity-60">·</span>
                <span className="inline-flex items-center gap-0.5">
                  <Camera className="w-3 h-3" strokeWidth={2} />
                  {r.photos}
                </span>
              </>
            )}
          </div>
        </div>
        <GoogleG className="w-4 h-4 mt-1 opacity-80" />
      </div>

      {/* Rating + time */}
      <div className="mt-4 flex items-center gap-2">
        <Stars n={r.rating} size={4} />
        <span className="text-xs text-neutral-500">{r.time}</span>
      </div>

      {/* Body */}
      <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-800 dark:text-neutral-200 flex-1">
        {r.text}
      </p>

      {/* Owner reply toggle */}
      {r.reply && (
        <button
          type="button"
          onClick={() => setShowReply((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E63FF] hover:underline self-start"
        >
          <MessageSquareQuote className="w-3.5 h-3.5" />
          {showReply ? t.reviews.hideReply : t.reviews.showReply}
          <ChevronDown className={`w-3 h-3 transition-transform ${showReply ? "rotate-180" : ""}`} />
        </button>
      )}

      {showReply && r.reply && (
        <div className="mt-3 pl-3 border-l-2 border-[#1E63FF]/40 bg-[#F5F5F7] dark:bg-white/[0.04] rounded-r-md py-2 pr-3">
          <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#0A0A0A] dark:text-white">
            TyresPro Ltd
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-[#1E63FF] text-white rounded uppercase tracking-wide">
              {t.reviews.owner}
            </span>
            <span className="text-neutral-500 font-normal">· {r.reply.time}</span>
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed text-neutral-700 dark:text-neutral-300">
            {r.reply.text}
          </p>
        </div>
      )}
    </article>
  );
}

export default function Reviews() {
  const { t } = useT();
  const live = useGoogleReviews({ rating: REVIEW_SUMMARY.rating, total: REVIEW_SUMMARY.count });
  // Duplicate list for seamless marquee loop
  const marqueeItems = [...REVIEWS, ...REVIEWS];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative bg-white dark:bg-[#0A0A0A] py-14 sm:py-20 lg:py-24 border-b border-black/5 overflow-hidden"
    >
      {/* Ambient decorative gradient — hidden on touch devices to save GPU on iOS */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#1E63FF]/6 blur-[120px] rounded-full hidden lg:block" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header + Summary */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10 lg:mb-12">
          <Reveal>
            <div className="flex items-center gap-2 mb-3">
              <GoogleG className="w-5 h-5" />
              <span className="overline text-neutral-500">{t.reviews.overline}</span>
            </div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A] dark:text-white leading-[1.05] max-w-xl">
              {t.reviews.heading1}<br />
              <span className="text-[#1E63FF]">{t.reviews.heading2}</span>
            </h2>
          </Reveal>

          {/* Summary card */}
          <Reveal direction="right" delay={100}>
            <div className="review-summary bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl p-5 sm:p-6 flex items-center gap-5 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.35)] w-full lg:w-auto">
              <div className="flex flex-col items-center gap-1">
                <GoogleG className="w-8 h-8" />
                <span className="text-[10px] font-semibold uppercase tracking-wider-2 text-neutral-500">Google</span>
              </div>
              <div className="h-14 w-px bg-black/10 dark:bg-white/10" />
              <div>
                <div className="flex items-baseline gap-2">
                  <span
                    data-testid="reviews-live-rating"
                    className="font-display font-semibold text-4xl sm:text-5xl text-[#0A0A0A] dark:text-white leading-none count-up"
                  >
                    {live.rating.toFixed(1)}
                  </span>
                  <Stars n={Math.round(live.rating)} size="lg" />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#4285F4]" strokeWidth={2.4} />
                  <span className="font-semibold text-[#0A0A0A] dark:text-white">{t.reviews.verified}</span>
                  {live.source === "live" && (
                    <span
                      data-testid="reviews-live-badge"
                      className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#0F9D58] uppercase tracking-wider-2"
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F9D58] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#0F9D58]"></span>
                      </span>
                      Live
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Marquee row */}
        <Reveal delay={40}>
          <div className="relative">
            <div
              data-testid="reviews-marquee"
              className="reviews-marquee flex items-stretch gap-4 sm:gap-5"
            >
              {marqueeItems.map((r, i) => (
                <ReviewCard key={`${r.name}-${i}`} r={r} t={t} />
              ))}
            </div>
          </div>
        </Reveal>

        {/* Bottom CTA row */}
        <Reveal delay={80}>
          <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-neutral-500 max-w-md">
              {t.reviews.footnote}
            </p>
            <a
              data-testid="reviews-write-btn"
              href={REVIEW_SUMMARY.writeReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 h-11 px-5 rounded-full border border-[#1E63FF] text-[#1E63FF] hover:bg-[#1E63FF] hover:text-white text-sm font-semibold transition-colors"
            >
              <GoogleG className="w-4 h-4" />
              {t.reviews.writeBtn}
            </a>
          </div>
        </Reveal>
      </div>

      <style>{`
        /* ---- Touch devices (mobile / iPad / iOS Safari): native horizontal swipe,
           no continuous animation, buttons remain tappable ---- */
        @media (hover: none) and (pointer: coarse) {
          .reviews-marquee {
            overflow-x: auto;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
            scroll-padding-left: 1rem;
            padding-left: 1rem;
            padding-right: 1rem;
            padding-bottom: 8px;
            width: 100%;
            max-width: 100%;
          }
          .reviews-marquee::-webkit-scrollbar { display: none; }
          .reviews-marquee .review-card {
            scroll-snap-align: start;
            /* No hover translate on touch — keeps cards stable for taps */
          }
          /* Marquee duplicates the cards for a seamless desktop loop; on touch
             we only want each review once so users don't see duplicates. */
          .reviews-marquee > article:nth-child(n+8) { display: none; }
        }

        /* ---- Desktop / pointer devices: auto-scrolling marquee ---- */
        @media (hover: hover) and (pointer: fine) {
          @keyframes reviewsMarquee {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          .reviews-marquee {
            width: max-content;
            animation: reviewsMarquee 55s linear infinite;
            will-change: transform;
          }
          .reviews-marquee:hover { animation-play-state: paused; }
          .review-card {
            transition: transform 300ms ease, border-color 300ms ease, box-shadow 300ms ease;
          }
          .review-card:hover {
            transform: translateY(-4px);
            border-color: rgba(30, 99, 255, 0.35);
            box-shadow: 0 24px 50px -24px rgba(30, 99, 255, 0.35);
          }
          html[dir="rtl"] .reviews-marquee { direction: ltr; }
        }

        @media (prefers-reduced-motion: reduce) {
          .reviews-marquee { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
